import { createMockableHook } from '@repo/common/utils/createMockableHook';
import queryClient from '../../queryClient';
import type {
	IApiListResponse,
	ISecodaEntity,
	IUseInfiniteListQueryHookArgs,
	IUseListQueryHookArgs,
} from '../../types';
import {
	SECODA_ENTITIES_NAMESPACE,
	secodaEntitiesQueryKeyFactory,
	useSecodaEntityInfiniteList,
	useSecodaEntityList,
} from '../secodaEntity';

export interface IUseResourceRelationListArgs<T>
	extends IUseListQueryHookArgs<ISecodaEntity, T> {
	entityId: string;
}

interface IUseResourceRelationInfiniteListArgs<T>
	extends IUseInfiniteListQueryHookArgs<ISecodaEntity, T> {
	entityId: string;
}

// We query from SecodaEntityViewset to re-use the permission and serialization logic.
function useResourceRelationListInternal<T = IApiListResponse<ISecodaEntity>>({
	entityId,
	options,
}: IUseResourceRelationListArgs<T>) {
	return useSecodaEntityList<ISecodaEntity, T>({
		filters: {
			related_entity_id: entityId,
		},
		options,
	});
}

export const [useResourceRelationList, MockUseResourceRelationList] =
	createMockableHook(useResourceRelationListInternal);

export const invalidateResourceRelationList = (entityId: string): void => {
	// Invalidate the regular list query
	queryClient.invalidateQueries({
		queryKey: secodaEntitiesQueryKeyFactory.list(1, {
			related_entity_id: entityId,
		}),
	});
};

export function useResourceRelationInfiniteList<
	T = IApiListResponse<ISecodaEntity>,
>({ entityId, options }: IUseResourceRelationInfiniteListArgs<T>) {
	return useSecodaEntityInfiniteList<ISecodaEntity, T>({
		filters: {
			related_entity_id: entityId,
		},
		options,
		queryKey: secodaEntitiesQueryKeyFactory.list(1, {
			related_entity_id: entityId,
		}),
		namespace: SECODA_ENTITIES_NAMESPACE,
	});
}
